import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';

import { ToastComponent } from '../../toast/toast.component';
import { AlertService } from '../../../services/alert.service';
import { PoliticianService } from '../../../services/politician.service';
import { EsService } from '../../../services/es.service';
import { Alert } from '../../models/alert.model';
import { Commission } from '../../models/commission.model';
import { ee, euCountries, organs, sources } from '../../constants';
import { doubleQuotesAreEven, formatSource, parenthesisAreEven, cleanText,
  setClassInvalidTpl, wildcardInQuotes } from '../../functions';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-edit-alert-modal',
  templateUrl: './edit-alert-modal.component.html',
  standalone: false
})
export class EditAlertModalComponent implements OnInit, OnDestroy {
  toast = inject(ToastComponent);
  private esService = inject(EsService);
  private alertService = inject(AlertService);
  private politicianService = inject(PoliticianService);


  @Input() commissions: Commission[] = [];
  @Input() alert: Alert = {};

  isLoading = {
    valid: false
  };
  errorMessages = {
    queryTooLong: 'Max length is 1000 characters!',
    wildcardInQuotes: 'Wildcard operator cannot be used inside double quotes!',
    parenthesisAreEven: 'Missing closing parenthesis!',
    doubleQuotesAreEven: 'Missing closing double quote!',
  };
  isAlertValid = false;
  sourcesFixed = [...sources]; // It needs to be immutable copy
  parties = [];
  partiesEU = [];
  euCountries = euCountries;
  organs = organs;
  formatSource = formatSource;
  setClassInvalidTpl = setClassInvalidTpl;

  ngOnInit(): void {
    // This is executed N+1 times, to fix eventually
    ee.on('showEditAlertModal', (alert: Alert) => this.alert = alert);
    this.getParties();
  }

  ngOnDestroy(): void {
    ee.off('showEditAlertModal', () => {});
  }

  getParties(): void {
    this.politicianService.getParties()
      .subscribe({
        next: res => {
          this.parties = res.filter(party => !party.parlamento_eu);
          this.partiesEU = res.filter(party => party.parlamento_eu);
        },
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  wildcardInQuotes(): boolean {
    return wildcardInQuotes(this.alert.query.keyword, this.alert.query.person, this.alert.query.noperson);
  }

  parenthesisAreEven(): boolean {
    return parenthesisAreEven(this.alert.query.keyword, this.alert.query.person, this.alert.query.noperson);
  }

  doubleQuotesAreEven(): boolean {
    return doubleQuotesAreEven(this.alert.query.keyword, this.alert.query.person, this.alert.query.noperson);
  }

  isModalInvalid(): boolean {
    return !this.alert._users?.length ||
      (this.alert.query.keyword?.length < 2 && this.alert.query.person?.length < 2) ||
      this.wildcardInQuotes() || !this.doubleQuotesAreEven() || !this.parenthesisAreEven();
  }

  checkSourceAlert(alert: Alert, source: string): void {
    const sourcesArray = alert.query.source.split(' ');
    if (!sourcesArray.includes(source)) {
      sourcesArray.push(source);
    } else {
      const index = sourcesArray.findIndex(s => s === source);
      sourcesArray.splice(index, 1);
    }
    alert.query.source = sourcesArray.join(' ');
  }

  validateAlert(alert: Alert): void {
    this.isLoading.valid = true;
    alert.query.keyword = cleanText(alert.query.keyword.trim());
    alert.query.person = cleanText(alert.query.person.trim());
    alert.query.noperson = cleanText(alert.query.noperson.trim());
    const params = {
      keyword: alert.query.keyword,
      person: alert.query.person,
      noperson: alert.query.noperson,
      source: alert.query.source,
      lang: alert.query.lang,
      party: alert.query.party,
      days: '1',
      organ: alert.query.organ,
      commission: alert.query.commission,
      country: alert.query.country,
    };
    this.esService.search(params)
      .pipe(finalize(() => this.isLoading.valid = false))
      .subscribe({
        next: res => {
          this.toast.setMessage('Alert query is valid', 'success');
          this.isAlertValid = true;
        },
        error: err => {
          this.toast.setMessage(err.error.message, 'danger');
          this.isAlertValid = false;
        },
      });
  }

  updateAlert(alert: Alert): void {
    const update = {
      ...alert,
      // Prevent Request Entity Too Large / Payload Too Large caused by these fields that are populated
      ids: [],
      _user: alert._user._id,
      _users: alert._users.map(user => user._id),
    };
    this.alertService.update(update)
      .pipe(finalize(() => {
        ee.emit('getAlerts');
        this.isAlertValid = false;
      }))
      .subscribe({
        next: res => this.toast.setMessage('Alert updated successfully.', 'success'),
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

}
